/* eslint-disable max-len */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FontIcon,
  IconButton,
  Label,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  mergeStyleSets,
  getTheme,
} from '@fluentui/react';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';
import { useLazyQuery } from '@apollo/react-hooks';
import { GET_STORAGE_TOKEN } from '../../utils';
import { BlobStorage } from '../blob-storage';
import { toastError, toastSuccess } from '../../utils/toast';
import { ConfirmDialog } from '../../components/parts';
import FileTypeIcon from './FileTypeIcon';

export interface IAttachment {
  id: number;
  blob_name: string;
  original_name: string;
  content_type?: string | null;
  size?: number | null;
  uploaded_at?: string | null;
  source_purchase_order_attachment_id?: number | null;
  uploaded_by_employee?: {
    first_name: string;
    last_name: string;
  } | null;
}

type Props = {
  title: string;
  endpoint?: string;
  blobPrefix?: string;
  canUpload?: boolean;
  canDelete?: boolean;
  allowDeleteCopied?: boolean;
  disabledMessage?: string;
  refreshKey?: string | number;
  hideHeader?: boolean;
};

const theme = getTheme();

const styles = mergeStyleSets({
  container: {
    borderRadius: 1,
    background: '#fafafa',
    overflow: 'hidden',
  },
  header: {
    padding: '12px 16px',
    background: theme.palette.neutralLighterAlt,
  },
  headerTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.neutralPrimary,
  },
  headerCount: {
    fontSize: 12,
    color: theme.palette.neutralSecondary,
    background: theme.palette.neutralLight,
    padding: '2px 8px',
    borderRadius: 10,
    fontWeight: 600,
  },
  dropzone: {
    margin: 12,
    padding: '16px 12px',
    borderRadius: 1,
    border: `2px dashed ${theme.palette.neutralTertiaryAlt}`,
    background: theme.palette.neutralLighterAlt,
    cursor: 'pointer',
    textAlign: 'center' as const,
    transition: 'background 0.15s ease, border-color 0.15s ease',
    selectors: {
      ':hover': {
        background: theme.palette.neutralLighter,
        borderColor: theme.palette.themePrimary,
      },
    },
  },
  dropzoneActive: {
    background: '#eef6fc',
    borderColor: theme.palette.themePrimary,
  },
  dropzoneTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.neutralPrimary,
  },
  dropzoneHint: {
    fontSize: 12,
    color: theme.palette.neutralSecondary,
  },
  list: {
    padding: '0 8px 8px 8px',
  },
  row: {
    padding: '6px 8px',
    borderRadius: 1,
    background: theme.palette.white,
    border: `1px solid ${theme.palette.neutralLight}`,
    transition: 'background 0.15s ease, border-color 0.15s ease',
    selectors: {
      ':hover': {
        background: theme.palette.neutralLighterAlt,
        borderColor: theme.palette.neutralQuaternaryAlt,
      },
    },
  },
  fileName: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 16,
    color: theme.palette.neutralPrimary,
    wordBreak: 'break-all' as const,
  },
  meta: {
    fontSize: 10,
    lineHeight: 14,
    color: theme.palette.neutralSecondary,
  },
  copiedBadge: {
    fontSize: 9,
    fontWeight: 600,
    color: '#107c10',
    background: '#dff6dd',
    padding: '1px 5px',
    borderRadius: 1,
    whiteSpace: 'nowrap' as const,
  },
  emptyState: {
    padding: '16px 8px',
    textAlign: 'center' as const,
    color: theme.palette.neutralSecondary,
    fontSize: 12,
  },
  loadingState: {
    padding: '10px 8px',
  },
});

const compactIconButtonStyles = {
  root: {
    width: 28,
    height: 28,
    minWidth: 28,
  },
  icon: {
    fontSize: 14,
  },
};

const formatBytes = (bytes?: number | null) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const sanitizeFileName = (fileName: string) => fileName.replace(/[^\w.\- ()]/g, '_');

const AttachmentList = ({
  title,
  endpoint,
  blobPrefix = 'attachments',
  canUpload = true,
  canDelete = true,
  allowDeleteCopied = false,
  disabledMessage = 'Sla eerst op voordat je bijlagen toevoegt.',
  refreshKey,
  hideHeader = false,
}: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<IAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<IAttachment | null>(null);
  const [acquireToken] = useLazyQuery(GET_STORAGE_TOKEN, {
    fetchPolicy: 'no-cache',
  });

  const backendEndpoint = endpoint
    ? `${process.env.REACT_APP_BACKEND_URI}${endpoint}`
    : undefined;

  const loadAttachments = useCallback(async () => {
    if (!backendEndpoint) return;

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(backendEndpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setAttachments(await response.json());
    } catch (error) {
      toastError(error instanceof Error ? error.message : 'Bijlagen laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [backendEndpoint, getAccessTokenSilently]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments, refreshKey]);

  const uploadFiles = async (files: FileList | File[] | null) => {
    if (!files || !backendEndpoint) return;
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    try {
      setUploading(true);
      const { data } = await acquireToken();
      const token = await getAccessTokenSilently();
      const fileStore = new BlobStorage(
        data.createStorageToken.accountName,
        data.createStorageToken.sasToken,
      );

      await Promise.all(
        fileArray.map(async file => {
          const fileName = `${blobPrefix}/${Date.now()}_${sanitizeFileName(file.name)}`;
          const buffer = await file.arrayBuffer();

          await fileStore.uploadBlob(
            'attachments',
            fileName,
            buffer,
            buffer.byteLength,
            {
              blobHTTPHeaders: {
                blobContentType: file.type || 'application/octet-stream',
              },
            },
          );

          const response = await fetch(backendEndpoint, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              blob_name: fileName,
              original_name: file.name,
              content_type: file.type || 'application/octet-stream',
              size: file.size,
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }),
      );

      toastSuccess(fileArray.length > 1 ? 'Bijlagen toegevoegd' : 'Bijlage toegevoegd');
      await loadAttachments();
    } catch (error) {
      toastError(error instanceof Error ? error.message : 'Bijlage uploaden mislukt');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadAttachment = async (attachment: IAttachment) => {
    try {
      const { data } = await acquireToken();
      const fileStore = new BlobStorage(
        data.createStorageToken.accountName,
        data.createStorageToken.sasToken,
      );
      await fileStore.downloadBlob('attachments', attachment.blob_name);
    } catch (error) {
      toastError(error instanceof Error ? error.message : 'Bijlage downloaden mislukt');
    }
  };

  const confirmDeleteAttachment = async () => {
    const attachment = attachmentToDelete;
    if (!attachment || !backendEndpoint) return;
    if (attachment.source_purchase_order_attachment_id && !allowDeleteCopied) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${backendEndpoint}/${attachment.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toastSuccess('Bijlage verwijderd');
      setAttachmentToDelete(null);
      await loadAttachments();
    } catch (error) {
      toastError(error instanceof Error ? error.message : 'Bijlage verwijderen mislukt');
    }
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (!backendEndpoint || uploading) return;
    uploadFiles(event.dataTransfer.files);
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!backendEndpoint || uploading) return;
    setIsDragging(true);
  };

  const onDragLeave = () => setIsDragging(false);

  const uploaderName = (attachment: IAttachment) => {
    if (!attachment.uploaded_by_employee) return null;
    return `${attachment.uploaded_by_employee.first_name} ${attachment.uploaded_by_employee.last_name}`.trim();
  };

  const sortedAttachments = useMemo(() => [...attachments].sort((a, b) => {
    const aDate = a.uploaded_at ? new Date(a.uploaded_at).getTime() : 0;
    const bDate = b.uploaded_at ? new Date(b.uploaded_at).getTime() : 0;
    return bDate - aDate;
  }), [attachments]);

  return (
    <div className={styles.container}>
      {!hideHeader && (
        <Stack
          horizontal
          verticalAlign='center'
          horizontalAlign='space-between'
          className={styles.header}
        >
          <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
            <FontIcon iconName='Attach' style={{ fontSize: 14, color: theme.palette.themePrimary }} />
            <Label className={styles.headerTitle}>{title}</Label>
          </Stack>
          {backendEndpoint && attachments.length > 0 && (
            <span className={styles.headerCount}>{attachments.length}</span>
          )}
        </Stack>
      )}

      {!backendEndpoint && (
        <div style={{ padding: 12 }}>
          <MessageBar messageBarType={MessageBarType.info}>{disabledMessage}</MessageBar>
        </div>
      )}

      {backendEndpoint && canUpload && (
        <div
          className={`${styles.dropzone} ${isDragging ? styles.dropzoneActive : ''}`}
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          role='button'
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              if (!uploading) fileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type='file'
            multiple
            style={{ display: 'none' }}
            disabled={uploading}
            onChange={event => uploadFiles(event.target.files)}
          />
          {uploading ? (
            <Stack horizontal horizontalAlign='center' verticalAlign='center' tokens={{ childrenGap: 10 }}>
              <Spinner size={SpinnerSize.small} />
              <Text className={styles.dropzoneTitle}>Uploaden...</Text>
            </Stack>
          ) : (
            <Stack horizontal horizontalAlign='center' verticalAlign='center' tokens={{ childrenGap: 12 }}>
              <FontIcon iconName='CloudUpload' style={{ fontSize: 22, color: theme.palette.themePrimary }} />
              <Stack tokens={{ childrenGap: 2 }} horizontalAlign='start'>
                <Text className={styles.dropzoneTitle}>
                  {isDragging ? 'Laat los om te uploaden' : 'Klik of sleep bestanden hierheen'}
                </Text>
                <Text className={styles.dropzoneHint}>
                  Meerdere bestanden tegelijk mogelijk
                </Text>
              </Stack>
            </Stack>
          )}
        </div>
      )}

      {backendEndpoint && (
        <div className={styles.list}>
          {loading && (
            <div className={styles.loadingState}>
              <Spinner size={SpinnerSize.small} label='Bijlagen laden...' labelPosition='right' />
            </div>
          )}

          {!loading && sortedAttachments.length === 0 && (
            <div className={styles.emptyState}>
              <Stack tokens={{ childrenGap: 4 }} horizontalAlign='center'>
                <FileTypeIcon icon='folder' size={32} idPrefix='empty-folder' />
                <Text styles={{ root: { color: theme.palette.neutralSecondary, fontSize: 12 } }}>
                  Nog geen bijlagen
                </Text>
              </Stack>
            </div>
          )}

          <Stack tokens={{ childrenGap: 4 }}>
            {sortedAttachments.map(attachment => {
              const isCopied = !!attachment.source_purchase_order_attachment_id;
              const uploaded = uploaderName(attachment);
              const showDelete = canDelete && (!isCopied || allowDeleteCopied);

              return (
                <Stack
                  key={attachment.id}
                  horizontal
                  verticalAlign='center'
                  tokens={{ childrenGap: 8 }}
                  className={styles.row}
                >
                  <FileTypeIcon
                    fileName={attachment.original_name}
                    contentType={attachment.content_type}
                    size={20}
                    idPrefix={`att-${attachment.id}`}
                  />

                  <Stack tokens={{ childrenGap: 2 }} grow>
                    <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
                      <Text className={styles.fileName}>{attachment.original_name}</Text>
                      {isCopied && (
                        <span className={styles.copiedBadge}>Van bestelbon</span>
                      )}
                    </Stack>
                    <Text className={styles.meta}>
                      {[
                        formatBytes(attachment.size),
                        attachment.uploaded_at
                          ? moment(attachment.uploaded_at).format('DD/MM/YYYY HH:mm')
                          : undefined,
                        uploaded,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </Text>
                  </Stack>

                  <Stack horizontal tokens={{ childrenGap: 0 }}>
                    <IconButton
                      iconProps={{ iconName: 'Download' }}
                      title='Download'
                      ariaLabel='Download'
                      styles={compactIconButtonStyles}
                      onClick={() => downloadAttachment(attachment)}
                    />
                    {showDelete && (
                      <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title='Verwijder'
                        ariaLabel='Verwijder'
                        styles={{
                          ...compactIconButtonStyles,
                          root: {
                            ...compactIconButtonStyles.root,
                            color: theme.palette.neutralSecondary,
                          },
                          rootHovered: { color: theme.palette.redDark, background: '#fde7e9' },
                        }}
                        onClick={() => setAttachmentToDelete(attachment)}
                      />
                    )}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </div>
      )}
      <ConfirmDialog
        hidden={!attachmentToDelete}
        onDismiss={() => setAttachmentToDelete(null)}
        onConfirm={confirmDeleteAttachment}
        title='Bijlage verwijderen'
        subText={
          attachmentToDelete
            ? `Weet je zeker dat je "${attachmentToDelete.original_name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`
            : undefined
        }
      />
    </div>
  );
};

export default AttachmentList;
